/**
 * Billing API Hooks
 *
 * TanStack Query hooks for subscription and usage.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getSubscriptionApiV1BillingSubscriptionGet,
  getUsageApiV1BillingUsageGet,
  getUsageHistoryApiV1BillingUsageHistoryGet,
  createCheckoutSessionApiV1BillingCheckoutPost,
} from '@/api';
import type { CreateCheckoutRequest } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const billingKeys = {
  all: ['billing'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  usage: () => [...billingKeys.all, 'usage'] as const,
  usageHistory: (params?: Record<string, unknown>) =>
    [...billingKeys.all, 'usageHistory', params] as const,
};

/**
 * Get current subscription details.
 */
export function useSubscription() {
  const { enabled: authEnabled } = useAuthEnabled();

  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: async () => {
      const response = await getSubscriptionApiV1BillingSubscriptionGet();
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch subscription'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get current usage summary.
 */
export function useUsage(options?: { enabled?: boolean }) {
  const { enabled: authEnabled } = useAuthEnabled(options?.enabled ?? true);

  return useQuery({
    queryKey: billingKeys.usage(),
    queryFn: async () => {
      const response = await getUsageApiV1BillingUsageGet();
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch usage'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get usage history.
 */
export function useUsageHistory(params?: { months?: number }) {
  const { enabled: authEnabled } = useAuthEnabled();

  return useQuery({
    queryKey: billingKeys.usageHistory(params),
    queryFn: async () => {
      const response = await getUsageHistoryApiV1BillingUsageHistoryGet({
        query: params,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch usage history'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Create a Stripe checkout session for subscription.
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: async (data: CreateCheckoutRequest) => {
      const response = await createCheckoutSessionApiV1BillingCheckoutPost({
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create checkout session'
        );
      }
      return response.data;
    },
  });
}
